import {takeLatestF, putAll, showSuccess, extractFormErrorsFromResponse, NullableKeys} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {genNakedPropOrCrash, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {CreateOrganizationBinding} from 'api/gen/CreateOrganizationBinding';
import {
    buildSubjectManagementCenterOptions,
    BuildSubjectManagementCenters,
} from 'app/admin/model/sagas/build-subject-management-center-options';
import {t} from 'app/translations';
import {formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {
    managementOrganizationFormName,
    ManagementOrganizationFormValues,
} from '../../types/management-organization-form-values';
import {adminAction, CreateManagementOrganizationAction} from '../action';

const te = t('common');
const title = te('createEntry');

const buildValuesForApi = (
    formValues: NullableKeys<ManagementOrganizationFormValues>,
    farmIds: Array<number> | null,
    organizationCenterIds: Array<number> | null,
): CreateOrganizationBinding => {
    const get = genNakedPropOrCrash(formValues);

    return {
        approvedFrom: get('approvedFrom'),
        approvedTo: opt(formValues).prop('approvedTo').orNull(),
        roleIds: get('roleIds'),
        organizationId: get('organizationId'),
        farmIds,
        organizationCenterIds,
        userProfileId: get('userProfileId'),
    };
};

function* execute({payload: {organizationId}}: CreateManagementOrganizationAction): SagaIterator {
    const formValues = (yield* select(formHelpers.formValues(managementOrganizationFormName)))
        .orCrash(`no form values: ${managementOrganizationFormName}`);

    const buildFarms: BuildSubjectManagementCenters = {
        centerIds: opt(formValues).prop('farmIds').orNull(),
        type: 'MANAGEMENT_ORGANIZATION_FARM',
    };
    const farmIds = yield* call(buildSubjectManagementCenterOptions, buildFarms);

    const buildCenters: BuildSubjectManagementCenters = {
        centerIds: opt(formValues).prop('organizationCenterIds').orNull(),
        type: 'MANAGEMENT_ORGANIZATION_CENTER',
    };
    const organizationCenterIds = yield* call(buildSubjectManagementCenterOptions, buildCenters);

    yield* put(formHelpers.startSubmit(managementOrganizationFormName));

    const response = yield* call(
        Api.addUserProfileOrganizationRoleBySubjectManager,
        buildValuesForApi(formValues, farmIds, organizationCenterIds),
    );

    if (response.isSuccess) {
        yield* put(adminAction.getManagementOrganizationsTableData(opt({action: 'refresh'}), organizationId));
        yield* put(showSuccess(title, te('createEntrySuccess'), false, 'createManagementOrganization'));
        yield* put(adminAction.setManagementSubjectModalVisible(null));
        yield* put(formHelpers.reset(managementOrganizationFormName));
    } else {
        yield putAll(showBeError(response, title));
        yield* put(formHelpers.stopSubmit(managementOrganizationFormName, extractFormErrorsFromResponse(response)));
    }
}

/** Runs the admin saga that creates an organization-management binding from the admin form. */
export function* createManagementOrganizationSaga(): SagaIterator {
    yield takeLatestF('admin/CREATE_MANAGEMENT_ORGANIZATION', execute);
}
